<template>
  <div class="ai-service-widget">
    <div class="service-icon" @click="openPanel" :title="text.headerTitle">
      <i class="el-icon-service"></i>
    </div>

    <transition name="ai-service-fade">
      <div class="service-panel" v-if="visible">
        <div class="service-header">
          <div class="service-header-left">
            <div class="bot-icon">
              <i class="el-icon-service"></i>
            </div>
            <div class="service-title">{{ text.headerTitle }}</div>
          </div>
          <div class="service-close" @click="closePanel">
            <i class="el-icon-close"></i>
          </div>
        </div>

        <div class="service-body">
          <template v-if="mode === 'chat'">
            <div class="welcome-card">
              <h3>{{ text.welcomeTitle }}</h3>
              <p class="card-text">{{ text.helpIntro }}</p>
              <ul class="bullet-list">
                <li v-for="(item, index) in text.helpItems" :key="`help-${index}`">{{ item }}</li>
              </ul>
              <p class="card-text">{{ text.exampleIntro }}</p>
              <ul class="bullet-list examples">
                <li v-for="(item, index) in text.exampleItems" :key="`example-${index}`">{{ item }}</li>
              </ul>
              <div class="quick-list">
                <span
                  class="quick-item"
                  v-for="(item, index) in text.quickQuestions"
                  :key="`quick-${index}`"
                  @click="quickAsk(item)"
                >
                  {{ item }}
                </span>
              </div>
            </div>

            <div class="message-list" v-if="messages.length">
              <div class="message-item" :class="item.role" v-for="(item, index) in messages" :key="`msg-${index}`">
                <div class="message-bubble">{{ item.content }}</div>
              </div>
            </div>
          </template>

          <template v-else>
            <div class="feedback-view">
              <div class="back-btn" @click="backToChat">{{ text.backToChat }}</div>
              <h3>{{ text.feedbackTitle }}</h3>
              <p class="feedback-desc">{{ text.feedbackDesc }}</p>

              <div class="feedback-form">
                <div class="form-group">
                  <label>{{ text.userField }} <span>*</span></label>
                  <input v-model.trim="feedbackForm.user" :placeholder="text.userPlaceholder" type="text" />
                </div>

                <div class="form-group">
                  <label>{{ text.emailField }} <span>*</span></label>
                  <input v-model.trim="feedbackForm.email" :placeholder="text.emailPlaceholder" type="text" />
                </div>

                <div class="form-group">
                  <label>{{ text.titleField }} <span>*</span></label>
                  <input v-model.trim="feedbackForm.title" :placeholder="text.titlePlaceholder" type="text" />
                </div>

                <div class="form-group">
                  <label>{{ text.descField }} <span>*</span></label>
                  <textarea v-model.trim="feedbackForm.desc" :placeholder="text.descPlaceholder"></textarea>
                </div>

                <div class="form-group">
                  <label>{{ text.uploadField }}</label>
                  <div class="upload-box" @click="triggerFileSelect">
                    <i class="el-icon-upload2"></i>
                    <span>{{ text.uploadBtn }}</span>
                  </div>
                  <input ref="fileInput" class="file-input" type="file" accept=".jpg,.jpeg,.png" @change="handleFileChange" />
                  <div class="upload-tip">{{ text.uploadTip }}</div>
                  <div class="upload-name">{{ selectedFileName || text.noFile }}</div>
                </div>

                <div class="submit-wrap">
                  <button class="submit-btn" type="button" @click="submitFeedback">{{ text.submit }}</button>
                </div>
              </div>
            </div>
          </template>
        </div>

        <div class="service-footer">
          <button class="feedback-toggle" type="button" @click="openFeedback">
            <i class="el-icon-message"></i>
            <span>{{ text.feedbackEntry }}</span>
          </button>
          <div class="footer-input">
            <input
              v-model="inputText"
              :placeholder="text.inputPlaceholder"
              type="text"
              @keyup.enter="sendMessage"
            />
            <button class="send-btn" type="button" @click="sendMessage">
              <i class="el-icon-s-promotion"></i>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'AiService',
  data () {
    return {
      visible: false,
      mode: 'chat',
      inputText: '',
      selectedFileName: '',
      messages: [],
      feedbackForm: {
        user: '',
        email: '',
        title: '',
        desc: ''
      },
      contentMap: {
        zh: {
          headerTitle: 'HeyMeta AI客服',
          welcomeTitle: '欢迎来到 HeyMeta AI 客服中心！',
          helpIntro: '我可以帮助您解答：',
          helpItems: ['注册与登录问题', '入金与出金操作', 'AI分析功能使用', '风险补偿机制说明'],
          exampleIntro: '您可以直接输入问题，例如：',
          exampleItems: ['如何注册账户？', '如何入金？', '如何交易股票？', '什么是赠金激励机制？'],
          quickQuestions: ['如何注册账户？', '如何入金？', '如何交易股票？', '什么是赠金激励机制？'],
          inputPlaceholder: '请输入您的问题...',
          feedbackEntry: '邮件反馈',
          backToChat: '返回对话',
          feedbackTitle: '联系邮件客服',
          feedbackDesc: '请填写以下信息，我们会尽快为您处理。为了更快定位问题，请尽量描述清楚您遇到的情况，并上传相关截图。',
          userField: '用户ID/用户名',
          userPlaceholder: '请输入您的用户ID或用户名',
          emailField: '邮箱',
          emailPlaceholder: '请输入您的邮箱地址',
          titleField: '问题标题',
          titlePlaceholder: '请简要概括您的问题，例如：无法下单、无法登录',
          descField: '问题描述',
          descPlaceholder: '请详细描述您遇到的问题，包括发生时间、操作步骤、页面提示等',
          uploadField: '上传截图',
          uploadBtn: '点击上传截图',
          uploadTip: '支持 jpg、png 格式，单张不超过 10MB',
          noFile: '未选择文件',
          submit: '提交',
          confirm: '确定',
          submitSuccess: '提交成功，我们会尽快通过邮件与您联系。',
          genericReply: '您好，您的问题已收到。当前为前端演示版本，后续可接入 AI 知识库与邮件处理流程。您也可以点击“邮件反馈”提交详细问题。',
          answers: {
            register: '注册账户后，您可以使用邮箱完成注册，并根据页面提示设置登录密码。注册完成后，请及时完善用户中心资料。',
            deposit: '入金时请先进入“我的资金-入金”页面，填写入金金额、交易哈希值，并按页面提示提交，等待审核完成即可。',
            trade: '您可在市场页面查看股票行情，选择股票后进入交易页面，支持查看行情、下单及管理持仓。请在交易前确认账户余额与市场状态。',
            bonus: '赠金激励机制主要包含每日成长赠金与风险补偿赠金。每日成长赠金可按规则累计，风险补偿赠金可在符合条件时用于亏损抵扣。'
          },
          validation: {
            user: '请输入用户ID或用户名',
            email: '请输入邮箱地址',
            emailInvalid: '请输入正确的邮箱地址',
            title: '请输入问题标题',
            desc: '请输入问题描述'
          }
        },
        en: {
          headerTitle: 'HeyMeta AI Support',
          welcomeTitle: 'Welcome to the HeyMeta AI Support Center!',
          helpIntro: 'I can help you with:',
          helpItems: ['Registration and login issues', 'Deposit and withdrawal operations', 'How to use AI analysis features', 'Risk compensation mechanism'],
          exampleIntro: 'You can ask directly, for example:',
          exampleItems: ['How do I register an account?', 'How do I make a deposit?', 'How do I trade stocks?', 'What is the bonus incentive mechanism?'],
          quickQuestions: ['How do I register an account?', 'How do I make a deposit?', 'How do I trade stocks?', 'What is the bonus incentive mechanism?'],
          inputPlaceholder: 'Please enter your question...',
          feedbackEntry: 'Email Feedback',
          backToChat: 'Back to Chat',
          feedbackTitle: 'Contact Email Support',
          feedbackDesc: 'Please fill in the information below and we will assist you as soon as possible. To help us locate the issue faster, please describe the problem clearly and upload relevant screenshots.',
          userField: 'User ID / Username',
          userPlaceholder: 'Please enter your user ID or username',
          emailField: 'Email',
          emailPlaceholder: 'Please enter your email address',
          titleField: 'Issue Title',
          titlePlaceholder: 'Please briefly summarize your issue, e.g. unable to place an order, unable to log in',
          descField: 'Issue Description',
          descPlaceholder: 'Please describe your issue in detail, including the time it occurred, the steps taken, and any on-screen prompts',
          uploadField: 'Upload Screenshot',
          uploadBtn: 'Click to Upload Screenshot',
          uploadTip: 'Supports jpg and png files, up to 10MB each',
          noFile: 'No file selected',
          submit: 'Submit',
          confirm: 'Confirm',
          submitSuccess: 'Submitted successfully. We will contact you by email as soon as possible.',
          genericReply: 'Hello, we have received your question. This is currently a front-end demo. AI knowledge base access and email workflow can be connected later. You can also click “Email Feedback” to submit full details.',
          answers: {
            register: 'You can register with your email address and set your login password by following the on-screen instructions. After registration, please complete your User Center profile in time.',
            deposit: 'To make a deposit, go to “My Funds - Deposit”, enter the deposit amount and transaction hash, then submit according to the page instructions and wait for the review to be completed.',
            trade: 'You can view stock quotes on the market page, select a stock to enter the trading page, and place orders or manage positions there. Please confirm your account balance and market status before trading.',
            bonus: 'The bonus incentive mechanism mainly includes the Daily Growth Bonus and the Risk Compensation Bonus. The Daily Growth Bonus can accumulate according to the rules, and the Risk Compensation Bonus can be used to offset losses when the conditions are met.'
          },
          validation: {
            user: 'Please enter your user ID or username',
            email: 'Please enter your email address',
            emailInvalid: 'Please enter a valid email address',
            title: 'Please enter the issue title',
            desc: 'Please enter the issue description'
          }
        },
        ja: {
          headerTitle: 'HeyMeta AIサポート',
          welcomeTitle: 'HeyMeta AI サポートセンターへようこそ！',
          helpIntro: '以下の内容をご案内できます：',
          helpItems: ['登録とログインに関する問題', '入金・出金の操作', 'AI分析機能の使い方', 'リスク補償メカニズムの説明'],
          exampleIntro: '以下のような質問を直接入力できます：',
          exampleItems: ['アカウントの登録方法は？', '入金方法は？', '株式の取引方法は？', 'ボーナス優遇制度とは何ですか？'],
          quickQuestions: ['アカウントの登録方法は？', '入金方法は？', '株式の取引方法は？', 'ボーナス優遇制度とは何ですか？'],
          inputPlaceholder: 'ご質問を入力してください...',
          feedbackEntry: 'メールフィードバック',
          backToChat: '会話に戻る',
          feedbackTitle: 'メールサポートへ連絡',
          feedbackDesc: '以下の情報をご記入ください。できるだけ早く対応いたします。問題を迅速に特定するため、状況をできるだけ詳しくご説明いただき、関連するスクリーンショットをアップロードしてください。',
          userField: 'ユーザーID / ユーザー名',
          userPlaceholder: 'ユーザーIDまたはユーザー名を入力してください',
          emailField: 'メールアドレス',
          emailPlaceholder: 'メールアドレスを入力してください',
          titleField: '問題タイトル',
          titlePlaceholder: '例：注文できない、ログインできない など、問題を簡潔に入力してください',
          descField: '問題の説明',
          descPlaceholder: '発生時刻、操作手順、画面表示などを含めて、問題の詳細を入力してください',
          uploadField: 'スクリーンショットをアップロード',
          uploadBtn: 'クリックしてスクリーンショットをアップロード',
          uploadTip: 'jpg、png 形式に対応、1ファイルあたり10MBまで',
          noFile: 'ファイルが選択されていません',
          submit: '送信',
          confirm: '確認する',
          submitSuccess: '送信が完了しました。できるだけ早くメールでご連絡いたします。',
          genericReply: 'こんにちは。ご質問を受け付けました。現在はフロントエンドのデモ版です。今後、AI知識ベースとメール処理フローを接続できます。「メールフィードバック」から詳細を送信することもできます。',
          answers: {
            register: 'メールアドレスで登録し、画面の案内に従ってログインパスワードを設定してください。登録後は、ユーザーセンターの情報を早めに補完してください。',
            deposit: '入金するには、「資金管理 - 入金」ページに入り、入金額と取引ハッシュを入力し、ページの案内に従って送信してください。審査完了後に反映されます。',
            trade: 'マーケットページで株価を確認し、銘柄を選択して取引ページに進むと、注文やポジション管理ができます。取引前に残高と市場状況をご確認ください。',
            bonus: 'ボーナス優遇制度には、主にデイリー成長ボーナスとリスク補償ボーナスがあります。デイリー成長ボーナスはルールに従って蓄積され、リスク補償ボーナスは条件を満たす場合に損失補填へ使用できます。'
          },
          validation: {
            user: 'ユーザーIDまたはユーザー名を入力してください',
            email: 'メールアドレスを入力してください',
            emailInvalid: '正しいメールアドレスを入力してください',
            title: '問題タイトルを入力してください',
            desc: '問題の説明を入力してください'
          }
        }
      }
    }
  },
  computed: {
    localeKey () {
      const locale = this.$i18n && this.$i18n.locale ? this.$i18n.locale : 'zh'
      if (locale.indexOf('en') === 0) return 'en'
      if (locale.indexOf('ja') === 0) return 'ja'
      return 'zh'
    },
    text () {
      return this.contentMap[this.localeKey]
    }
  },
  methods: {
    openPanel () {
      this.visible = true
    },
    closePanel () {
      this.visible = false
      this.mode = 'chat'
    },
    openFeedback () {
      this.visible = true
      this.mode = 'feedback'
    },
    backToChat () {
      this.mode = 'chat'
    },
    quickAsk (question) {
      this.inputText = question
      this.sendMessage()
    },
    sendMessage () {
      const value = String(this.inputText || '').trim()
      if (!value) return
      this.visible = true
      this.mode = 'chat'
      this.messages.push({
        role: 'user',
        content: value
      })
      this.inputText = ''
      const reply = this.getAutoReply(value)
      setTimeout(() => {
        this.messages.push({
          role: 'assistant',
          content: reply
        })
      }, 250)
    },
    getAutoReply (question) {
      const lower = String(question || '').toLowerCase()
      if (lower.indexOf('注册') > -1 || lower.indexOf('register') > -1 || question.indexOf('登録') > -1) {
        return this.text.answers.register
      }
      if (lower.indexOf('入金') > -1 || lower.indexOf('deposit') > -1) {
        return this.text.answers.deposit
      }
      if (lower.indexOf('交易') > -1 || lower.indexOf('trade') > -1 || question.indexOf('取引') > -1) {
        return this.text.answers.trade
      }
      if (lower.indexOf('赠金') > -1 || lower.indexOf('bonus') > -1 || question.indexOf('ボーナス') > -1) {
        return this.text.answers.bonus
      }
      return this.text.genericReply
    },
    triggerFileSelect () {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click()
      }
    },
    handleFileChange (event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      if (file.size / 1024 / 1024 > 10) {
        this.$alert(this.text.uploadTip, {
          confirmButtonText: this.text.confirm,
          center: true,
          customClass: 'warning-alert'
        })
        event.target.value = ''
        return
      }
      this.selectedFileName = file.name
    },
    submitFeedback () {
      const form = this.feedbackForm
      if (!form.user) {
        this.$message.warning(this.text.validation.user)
        return
      }
      if (!form.email) {
        this.$message.warning(this.text.validation.email)
        return
      }
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
      if (!emailRegex.test(form.email)) {
        this.$message.warning(this.text.validation.emailInvalid)
        return
      }
      if (!form.title) {
        this.$message.warning(this.text.validation.title)
        return
      }
      if (!form.desc) {
        this.$message.warning(this.text.validation.desc)
        return
      }
      this.$alert(this.text.submitSuccess, {
        confirmButtonText: this.text.confirm,
        center: true,
        customClass: 'success-alert'
      })
      this.feedbackForm = {
        user: '',
        email: '',
        title: '',
        desc: ''
      }
      this.selectedFileName = ''
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
      this.mode = 'chat'
    }
  }
}
</script>

<style scoped lang="scss">
.ai-service-widget {
  position: relative;
  z-index: 9998;

  .service-icon {
    width: 34px;
    height: 34px;
    margin-top: -5px;
    margin-right: 24px;
    border: 1px solid rgba(0, 207, 255, 0.8);
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #00d7ff;
    font-size: 20px;
    background: linear-gradient(180deg, rgba(0, 93, 121, 0.7), rgba(0, 35, 49, 0.95));
    box-shadow: inset 0 0 10px rgba(0, 213, 255, 0.22), 0 0 10px rgba(0, 213, 255, 0.24);

    &:hover {
      color: #fff;
      border-color: #57e9ff;
      box-shadow: inset 0 0 14px rgba(0, 213, 255, 0.4), 0 0 15px rgba(0, 213, 255, 0.4);
    }
  }

  .service-panel {
    position: fixed;
    right: 24px;
    top: 76px;
    width: 390px;
    height: 690px;
    z-index: 9999;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    color: #b8e9f4;
    background:
      linear-gradient(135deg, transparent 13px, #062430 0) top left,
      linear-gradient(225deg, transparent 13px, #062430 0) top right,
      linear-gradient(315deg, transparent 13px, #062430 0) bottom right,
      linear-gradient(45deg, transparent 13px, #062430 0) bottom left;
    background-size: 51% 51%;
    background-repeat: no-repeat;
    border: 1px solid rgba(0, 202, 241, 0.65);
    box-shadow: 0 0 0 2px rgba(0, 82, 105, 0.55), 0 0 24px rgba(0, 196, 238, 0.25), 0 18px 45px rgba(0, 0, 0, 0.52);

    &::before,
    &::after {
      content: '';
      position: absolute;
      pointer-events: none;
      z-index: 1;
    }

    &::before {
      left: 12px;
      right: 12px;
      top: 8px;
      height: 2px;
      background: linear-gradient(90deg, transparent, #00d9ff, transparent);
      box-shadow: 0 0 10px rgba(0, 217, 255, 0.8);
    }

    &::after {
      left: 12px;
      right: 12px;
      bottom: 8px;
      height: 1px;
      background: linear-gradient(90deg, transparent, rgba(0, 217, 255, 0.8), transparent);
    }
  }

  .service-header {
    position: relative;
    z-index: 2;
    height: 66px;
    flex-shrink: 0;
    padding: 0 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: #d9f9ff;
    background: linear-gradient(180deg, rgba(0, 83, 108, 0.92), rgba(1, 43, 57, 0.96));
    border-bottom: 1px solid rgba(0, 205, 241, 0.65);
    box-shadow: inset 0 -8px 20px rgba(0, 190, 226, 0.05);
  }

  .service-header-left {
    display: flex;
    align-items: center;
  }

  .bot-icon,
  .service-close {
    width: 34px;
    height: 34px;
    border: 1px solid rgba(0, 218, 255, 0.75);
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #00d8ff;
    background: rgba(0, 36, 48, 0.72);
    box-shadow: inset 0 0 10px rgba(0, 213, 255, 0.15);
  }

  .service-close {
    cursor: pointer;

    &:hover {
      color: #fff;
      border-color: #62ecff;
    }
  }

  .service-title {
    margin-left: 12px;
    font-size: 17px;
    font-weight: 600;
    color: #d8f9ff;
    text-shadow: 0 0 8px rgba(0, 215, 255, 0.4);
  }

  .service-body {
    position: relative;
    z-index: 2;
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    background: rgba(2, 25, 34, 0.86);
    scrollbar-color: #0089aa #031a22;
    scrollbar-width: thin;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: #031a22;
    }

    &::-webkit-scrollbar-thumb {
      background: #0089aa;
      border-radius: 3px;
    }
  }

  .welcome-card,
  .feedback-view {
    position: relative;
    padding: 18px 18px 20px;
    border: 1px solid rgba(0, 162, 198, 0.62);
    background: linear-gradient(180deg, rgba(5, 48, 62, 0.92), rgba(2, 31, 42, 0.96));
    box-shadow: inset 0 0 20px rgba(0, 206, 244, 0.05), 0 8px 20px rgba(0, 0, 0, 0.2);

    &::before,
    &::after {
      content: '';
      position: absolute;
      width: 25px;
      height: 2px;
      background: #00d5ff;
      box-shadow: 0 0 7px rgba(0, 213, 255, 0.75);
    }

    &::before {
      top: -1px;
      left: 12px;
    }

    &::after {
      right: 12px;
      bottom: -1px;
    }
  }

  .welcome-card h3,
  .feedback-view h3 {
    margin-bottom: 14px;
    color: #e3fbff;
    font-size: 16px;
    line-height: 1.55;
  }

  .card-text,
  .feedback-desc,
  .upload-tip,
  .upload-name,
  .back-btn {
    color: #8fc3d0;
    font-size: 14px;
    line-height: 1.7;
  }

  .bullet-list {
    margin: 8px 0 12px;
    padding-left: 0;
    list-style: none;

    li {
      margin-bottom: 4px;
      color: #c5edf5;
      font-size: 14px;
      line-height: 1.8;

      &::before {
        content: '•';
        margin-right: 8px;
        color: #00d8ff;
      }
    }
  }

  .quick-list {
    display: flex;
    flex-wrap: wrap;
    gap: 9px;
    margin-top: 14px;
  }

  .quick-item {
    padding: 7px 11px;
    border: 1px solid rgba(0, 192, 232, 0.72);
    border-radius: 2px;
    color: #31dfff;
    font-size: 13px;
    cursor: pointer;
    background: rgba(0, 65, 84, 0.45);
    box-shadow: inset 0 0 8px rgba(0, 215, 255, 0.08);

    &:hover {
      color: #fff;
      border-color: #57e9ff;
      background: rgba(0, 111, 138, 0.55);
    }
  }

  .message-list {
    margin-top: 14px;
  }

  .message-item {
    display: flex;
    margin-bottom: 10px;

    &.user {
      justify-content: flex-end;

      .message-bubble {
        color: #e9fdff;
        border-color: rgba(0, 219, 255, 0.8);
        background: linear-gradient(180deg, rgba(0, 108, 136, 0.9), rgba(0, 65, 83, 0.95));
      }
    }

    &.assistant {
      justify-content: flex-start;

      .message-bubble {
        color: #bdeaf3;
        background: rgba(4, 43, 56, 0.95);
      }
    }
  }

  .message-bubble {
    max-width: 90%;
    padding: 11px 13px;
    border: 1px solid rgba(0, 143, 177, 0.72);
    border-radius: 3px;
    font-size: 14px;
    line-height: 1.7;
    word-break: break-word;
    box-shadow: inset 0 0 12px rgba(0, 206, 244, 0.04), 0 5px 12px rgba(0, 0, 0, 0.18);
  }

  .back-btn {
    display: inline-block;
    margin-bottom: 10px;
    color: #00d7ff;
    cursor: pointer;

    &:hover {
      color: #fff;
    }
  }

  .feedback-form {
    margin-top: 12px;
  }

  .form-group {
    margin-bottom: 16px;

    label {
      display: block;
      margin-bottom: 8px;
      color: #bfeaf2;
      font-size: 14px;
      font-weight: 500;

      span {
        color: #ff5d72;
      }
    }

    input,
    textarea {
      width: 100%;
      box-sizing: border-box;
      padding: 11px 13px;
      border: 1px solid rgba(0, 155, 190, 0.7);
      border-radius: 2px;
      outline: none;
      color: #d8f8ff;
      font-size: 14px;
      background: rgba(0, 27, 37, 0.88);
      box-shadow: inset 0 0 12px rgba(0, 202, 241, 0.04);

      &::placeholder {
        color: #527c87;
      }

      &:focus {
        border-color: #00d8ff;
        box-shadow: inset 0 0 10px rgba(0, 213, 255, 0.08), 0 0 8px rgba(0, 213, 255, 0.18);
      }
    }

    textarea {
      min-height: 120px;
      resize: vertical;
    }
  }

  .upload-box {
    height: 96px;
    border: 1px dashed rgba(0, 195, 232, 0.78);
    border-radius: 2px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #21dbff;
    background: rgba(0, 57, 73, 0.34);

    i {
      margin-bottom: 8px;
      font-size: 28px;
    }

    span {
      font-size: 14px;
      font-weight: 500;
    }

    &:hover {
      border-color: #5beaff;
      background: rgba(0, 83, 104, 0.42);
    }
  }

  .file-input {
    display: none;
  }

  .upload-tip {
    margin-top: 10px;
  }

  .upload-name {
    margin-top: 4px;
    color: #668e98;
  }

  .submit-wrap {
    display: flex;
    justify-content: center;
    margin-top: 12px;
  }

  .submit-btn,
  .send-btn,
  .feedback-toggle {
    border: 1px solid rgba(0, 209, 248, 0.8);
    color: #dffbff;
    cursor: pointer;
    background: linear-gradient(180deg, rgba(0, 115, 145, 0.94), rgba(0, 58, 75, 0.98));
    box-shadow: inset 0 0 12px rgba(0, 217, 255, 0.14), 0 0 9px rgba(0, 217, 255, 0.18);

    &:hover {
      border-color: #61edff;
      background: linear-gradient(180deg, rgba(0, 142, 175, 0.98), rgba(0, 75, 94, 1));
    }
  }

  .submit-btn {
    min-width: 112px;
    height: 40px;
    border-radius: 2px;
    font-size: 15px;
  }

  .service-footer {
    position: relative;
    z-index: 2;
    flex-shrink: 0;
    padding: 11px 12px 15px;
    border-top: 1px solid rgba(0, 173, 207, 0.52);
    background: linear-gradient(180deg, rgba(3, 35, 46, 0.98), rgba(1, 24, 33, 0.99));
  }

  .feedback-toggle {
    height: 32px;
    margin-bottom: 10px;
    padding: 0 12px;
    border-radius: 2px;
    display: inline-flex;
    align-items: center;
    font-size: 13px;

    i {
      margin-right: 6px;
    }
  }

  .footer-input {
    height: 54px;
    padding: 0 8px 0 13px;
    border: 1px solid rgba(0, 165, 202, 0.72);
    border-radius: 2px;
    display: flex;
    align-items: center;
    background: rgba(0, 25, 34, 0.94);
    box-shadow: inset 0 0 14px rgba(0, 207, 245, 0.04);

    input {
      flex: 1;
      border: none;
      outline: none;
      color: #d9f8ff;
      font-size: 14px;
      background: transparent;

      &::placeholder {
        color: #527b85;
      }
    }
  }

  .send-btn {
    width: 40px;
    height: 40px;
    border-radius: 2px;
    font-size: 19px;
  }
}

.ai-service-fade-enter-active,
.ai-service-fade-leave-active {
  transition: all 0.2s ease;
}

.ai-service-fade-enter,
.ai-service-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@media only screen and (max-width: 755px) {
  .ai-service-widget {
    .service-icon {
      width: 46px;
      height: 46px;
      margin-right: 18px;
      font-size: 24px;
    }

    .service-panel {
      right: 8px;
      left: 8px;
      top: 92px;
      width: auto;
      height: calc(100vh - 104px);
    }
  }
}
</style>
